/**
 * Extract individual skills from a comma-separated string
 * @param {string} skillsString - Comma-separated list of skills
 * @returns {Array} Array of individual skills
 */
export const extractSkills = (skillsString) => {
  if (!skillsString) return [];
  return skillsString.split(',').map(skill => skill.trim());
};

/**
 * Extract client name from a query string
 * @param {string} query - The query string to extract from
 * @returns {string|null} The extracted client name or null
 */
export const extractClientName = (query) => {
  const clients = ['TechCorp', 'DataPrime', 'DigiTech', 'CloudNine', 'NextGen Solutions'];
  
  // Check for exact client name match
  for (const client of clients) {
    if (query.toLowerCase().includes(client.toLowerCase())) {
      return client;
    }
  }
  
  // Try to extract name after "for"
  const forIndex = query.toLowerCase().indexOf(' for ');
  if (forIndex !== -1) {
    return query.substring(forIndex + 5).trim();
  }
  
  return null;
};

/**
 * Format a number as currency
 * @param {number} value - The value to format
 * @param {string} currency - The currency code (default: EUR)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = 'EUR') => {
  const symbols = {
    EUR: '€',
    USD: '$',
    GBP: '£',
  };
  
  const symbol = symbols[currency] || currency;
  return `${symbol}${value.toLocaleString()}`;
};

/**
 * Calculate the time difference between two dates in days
 * @param {string} startDate - Start date string
 * @param {string} endDate - End date string
 * @returns {number} Number of days between dates
 */
export const calculateDaysBetween = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

/**
 * Group data by a specified key
 * @param {Array} data - Array of objects to group
 * @param {string} key - The key to group by
 * @returns {Object} Grouped data object
 */
export const groupBy = (data, key) => {
  return data.reduce((result, item) => {
    const groupKey = item[key];
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {});
};

/**
 * Calculate average value from an array of objects based on a specific key
 * @param {Array} data - Array of objects
 * @param {string} key - The key to calculate average for
 * @returns {number} The calculated average
 */
export const calculateAverage = (data, key) => {
  if (!data || data.length === 0) return 0;
  
  const sum = data.reduce((total, item) => {
    const value = item[key];
    return total + (typeof value === 'number' ? value : 0);
  }, 0);
  
  return Math.round(sum / data.length);
};

/**
 * Get a color based on a value within a range
 * @param {number} value - The value to get color for
 * @param {number} min - Minimum value in range
 * @param {number} max - Maximum value in range
 * @returns {string} CSS color string
 */
export const getColorByValue = (value, min, max) => {
  // Default to mid-range if not a number
  if (typeof value !== 'number') return '#f59e0b';
  
  // Normalize value to 0-1 range
  const normalized = Math.max(0, Math.min(1, (value - min) / (max - min)));
  
  // Color ranges: red -> yellow -> green
  if (normalized < 0.5) {
    // Red to yellow gradient
    const r = 255;
    const g = Math.floor(normalized * 2 * 255);
    return `rgb(${r}, ${g}, 0)`;
  } else {
    // Yellow to green gradient
    const r = Math.floor((1 - (normalized - 0.5) * 2) * 255);
    const g = 255;
    return `rgb(${r}, ${g}, 0)`;
  }
};

/**
 * Sort array of objects by a specific key
 * @param {Array} data - Array of objects to sort
 * @param {string} key - The key to sort by
 * @param {boolean} ascending - Sort direction (true for ascending, false for descending)
 * @returns {Array} Sorted array
 */
export const sortByKey = (data, key, ascending = true) => {
  return [...data].sort((a, b) => {
    const valueA = a[key];
    const valueB = b[key];
    
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return ascending 
        ? valueA.localeCompare(valueB) 
        : valueB.localeCompare(valueA);
    }
    
    return ascending 
      ? (valueA - valueB)
      : (valueB - valueA);
  });
};

/**
 * Filter data based on search term across multiple fields
 * @param {Array} data - Array of objects to filter
 * @param {string} searchTerm - Term to search for
 * @param {Array} fields - Fields to search in
 * @returns {Array} Filtered array
 */
export const searchAcrossFields = (data, searchTerm, fields) => {
  if (!searchTerm) return data;
  
  const term = searchTerm.toLowerCase();
  
  return data.filter(item => {
    return fields.some(field => {
      const value = item[field];
      if (typeof value === 'string') {
        return value.toLowerCase().includes(term);
      }
      return false;
    });
  });
};
